"use client";

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import { BASE_URL, CDN_URL } from "../config/apiconfig";

/* ---------------- CONTEXT ---------------- */

const CustomersContext = createContext(null);

/* ---------------- PROVIDER ---------------- */

export const CustomersProvider = ({ children }) => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /* ---------------- AUTH HEADERS ---------------- */

  const getHeaders = () => {
    const token = localStorage.getItem("token");
    return {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
  };

  /* ---------------- NORMALIZE CUSTOMER ---------------- */

 const normalizeCustomer = (c) => ({
  id: String(c.id ?? c.user_id ?? ""),
  firstName: c.first_name ?? "",
  lastName: c.last_name ?? "",
  fullName: `${c.first_name ?? ""} ${c.last_name ?? ""}`.trim() || "—",
  email: c.email ?? "",
  phone: c.phone ?? "",
  profileImage: c.profile_image
    ? `${CDN_URL}/${c.profile_image}`
    : "",

      dateOfBirth: c.dob ?? c.date_of_birth ?? c.DOB ?? c.birth_date ?? "",
  gender: c.gender ?? c.Gender ?? c.sex ?? "",
 address: {
  street: c.address ?? "",   // backend "address"
  city: c.city ?? "",
  state: c.state ?? "",
  pincode: c.pincode ?? "",
  country: c.country ?? "India",
},
  notes: c.notes ?? "", 
  totalOrders: Number(c.total_orders ?? c.orders_count ?? 0),
  totalSpent: Number(c.total_spent ?? c.total_amount ?? 0),
  lastOrderDate: c.last_order_date ?? null,

  customerType: c.customer_type ?? "regular",
 status: (
  c.status === 1 ||
  c.status === "1" ||
  c.status === true ||
  c.status === "true" ||
  String(c.status).toLowerCase() === "active"
)
  ? "active"
  : "inactive",

  registrationDate: c.created_at ?? null,
});


  /* ---------------- FETCH CUSTOMERS ---------------- */

const fetchCustomers = useCallback(async () => {
  try {
    setLoading(true);
    setError(null);

    const res = await fetch(`${BASE_URL}/customers`, {
      method: "GET",
      headers: getHeaders(),
    });

    if (!res.ok) {
      throw new Error("Failed to fetch customers");
    }

    const json = await res.json();
    const list = Array.isArray(json?.data) ? json.data : [];

    setCustomers(list.map(normalizeCustomer));
  } catch (err) {
    console.error("❌ Fetch failed:", err);
    setError(err.message);
  } finally {
    setLoading(false);
  }
}, []);

useEffect(() => {
  fetchCustomers();
}, [fetchCustomers]);
const generatePassword = () => {
  // Cryptographically secure, mixed character classes (not Math.random).
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$%&*";
  const arr = new Uint32Array(14);
  (window.crypto || window.msCrypto).getRandomValues(arr);
  return Array.from(arr, (n) => chars[n % chars.length]).join("");
};

const toApiPayload = (payload) => ({
  first_name: payload.firstName ?? "",
  last_name: payload.lastName ?? "",
  email: payload.email ?? "",
  phone: payload.phone ?? "",

  password: payload.password || generatePassword(),
address: payload.address?.street ?? "",
  city: payload.address?.city ?? "",
  state: payload.address?.state ?? "",
  pincode: payload.address?.pincode ?? "",   // ✅ correct
  country: payload.address?.country ?? "India",

  status: payload.status === "active" ? "active" : "inactive",
});


const addCustomer = async (formData) => {
  const res = await fetch(`${BASE_URL}/customers`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
    body: formData,
  });

  const json = await res.json();

  // 🔥 HANDLE BOTH CASES
  if (!res.ok || json.status === false) {
    throw json;
  }

  await fetchCustomers();
  return json;
};





  /* ---------------- UPDATE CUSTOMER ---------------- */

const updateCustomer = async (id, formData) => {
  console.log("📤 UPDATE payload:", id, formData);

  const res = await fetch(`${BASE_URL}/customers/${id}`, {
    method: "POST", 
    headers: {
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
    body: formData,
  });

  const json = await res.json();
  console.log("📥 UPDATE response:", json);

  if (!res.ok || json.status === false) {
    throw json;
  }

  await fetchCustomers();
  return json;
};





  /* ---------------- DELETE CUSTOMER ---------------- */

  const deleteCustomer = async (id) => {
    await fetch(`${BASE_URL}/customers/${id}`, {
      method: "DELETE",
      headers: getHeaders(),
    });

    setCustomers((prev) => prev.filter((c) => c.id !== id));
  };

  /* ---------------- TOGGLE STATUS (UI ONLY) ---------------- */

  const toggleCustomerStatus = (id) => {
    setCustomers((prev) =>
      prev.map((c) =>
        c.id === id
          ? {
              ...c,
              status: c.status === "active" ? "inactive" : "active",
            }
          : c
      )
    );
  };

  /* ---------------- INITIAL LOAD ---------------- */

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  /* ---------------- PROVIDER VALUE ---------------- */

  return (
    <CustomersContext.Provider
      value={{
        customers,
        loading,
        error,
        fetchCustomers,
        addCustomer,
        updateCustomer,
        deleteCustomer,
        toggleCustomerStatus,
      }}
    >
      {children}
    </CustomersContext.Provider>
  );
};

/* ---------------- HOOK ---------------- */

export const useCustomers = () => {
  const ctx = useContext(CustomersContext);
  if (!ctx) {
    throw new Error(
      "useCustomers must be used within CustomersProvider"
    );
  }
  return ctx;
};
